import React, { useEffect, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import rtlPlugin from 'stylis-plugin-rtl';
import { prefixer } from 'stylis';
import { useTranslation } from 'react-i18next';
import { isRtlLanguage } from './i18n';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { EmployeeAuthProvider, useEmployeeAuth } from './hooks/useEmployeeAuth';
import LoadingSpinner from './components/LoadingSpinner';
import './utils/token-helper';

// Pages
import Login from './pages/Login';
import EmployeeLogin from './pages/EmployeeLogin';
import EmployeeChangePassword from './pages/EmployeeChangePassword';
import EmployeeDashboard from './pages/EmployeeDashboard';
import EmployeeNotifications from './pages/EmployeeNotifications';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import AccountSettings from './pages/AccountSettings';
import Vacations from './pages/Vacations';
import Holidays from './pages/Holidays';
import Cameras from './pages/Cameras';
import Departments from './pages/Departments';
import Calendars from './pages/Calendars';
const Scheduling = React.lazy(() => import('./pages/Scheduling'));
const PayrollSettings = React.lazy(() => import('./pages/payroll/PayrollSettings'));
const PayComponents = React.lazy(() => import('./pages/payroll/PayComponents'));
const PayrollExceptions = React.lazy(() => import('./pages/payroll/PayrollExceptions'));
const PayrollPeriods = React.lazy(() => import('./pages/payroll/PayrollPeriods'));
const PayrollPeriodDetail = React.lazy(() => import('./pages/payroll/PayrollPeriodDetail'));
const PayslipDetail = React.lazy(() => import('./pages/payroll/PayslipDetail'));
const PayrollOverview = React.lazy(() => import('./pages/payroll/PayrollOverview'));
const EmployeePayslips = React.lazy(() => import('./pages/EmployeePayslips'));
const EmployeePayslipDetail = React.lazy(() => import('./pages/EmployeePayslipDetail'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Analytics = React.lazy(() => import('./pages/Analytics'));
const Reports = React.lazy(() => import('./pages/Reports'));
const CustomReports = React.lazy(() => import('./pages/CustomReports'));
const DailyAttendance = React.lazy(() => import('./pages/DailyAttendance'));
const EmployeeTimeline = React.lazy(() => import('./pages/EmployeeTimeline'));
const CameraSnapshots = React.lazy(() => import('./pages/CameraSnapshots'));
const FaceValidation = React.lazy(() => import('./pages/FaceValidation'));
const Employees = React.lazy(() => import('./pages/Employees'));
const DeskOccupancy = React.lazy(() => import('./pages/DeskOccupancy'));
import EdgeDevices from './pages/EdgeDevices';
import PPE from './pages/PPE';
import PPEDetections from './pages/PPEDetections';
import PPESummary from './pages/PPESummary';
import Policies from './pages/Policies';
import Settings from './pages/Settings';
import RoleManagement from './pages/RoleManagement';
import UserManagement from './pages/UserManagement';
import UserCompanyAssignments from './pages/UserCompanyAssignments';
import CompanyManagement from './pages/CompanyManagement';
import HierarchyManagement from './pages/HierarchyManagement';
import AppearanceThresholds from './pages/AppearanceThresholds';
import Notifications from './pages/Notifications';
import PPEDepartmentSettings from './pages/PPEDepartmentSettings';
import PPEViolations from './pages/PPEViolations';
import SnapshotSettings from './pages/SnapshotSettings';
import AnalysisPrompts from './pages/AnalysisPrompts';
import DeskZones from './pages/DeskZones';
import { buildCompanyUrl, decodePathSegment, switchCompanyInUrl } from './utils/navigation';

const rtlCache = createCache({
  key: 'muirtl',
  stylisPlugins: [prefixer, rtlPlugin],
});

const ltrCache = createCache({
  key: 'muiltr',
});

const readDesignToken = (name: string) => getComputedStyle(document.documentElement).getPropertyValue(name).trim();

function createAppTheme(direction: 'ltr' | 'rtl') {
  const appThemeTokens = {
    primary: readDesignToken('--an-primary'),
    primaryLight: readDesignToken('--an-primary-light'),
    primaryDark: readDesignToken('--an-primary-dark'),
    accent: readDesignToken('--an-accent'),
    accentDark: readDesignToken('--an-accent'),
    surface: readDesignToken('--an-surface'),
    surfaceMuted: readDesignToken('--an-surface-muted'),
    background: readDesignToken('--an-background'),
    textPrimary: readDesignToken('--an-text-primary'),
    textSecondary: readDesignToken('--an-text-secondary'),
    textTertiary: readDesignToken('--an-text-tertiary'),
    border: readDesignToken('--an-border'),
    borderLight: readDesignToken('--an-border-light'),
    success: readDesignToken('--an-success'),
    successLight: readDesignToken('--an-success-light'),
    warning: readDesignToken('--an-warning'),
    warningLight: readDesignToken('--an-warning-light'),
    error: readDesignToken('--an-error'),
    errorLight: readDesignToken('--an-error-light'),
    info: readDesignToken('--an-info'),
    infoLight: readDesignToken('--an-info-light'),
    fontSans: readDesignToken('--an-font-sans'),
  };

  return createTheme({
    direction,
    shape: {
      borderRadius: 10,
    },
    palette: {
      mode: 'light',
      primary: {
        main: appThemeTokens.primary,
        light: appThemeTokens.primaryLight,
        dark: appThemeTokens.primaryDark,
        contrastText: appThemeTokens.surface,
      },
      secondary: {
        main: appThemeTokens.accent,
        light: appThemeTokens.accent,
        dark: appThemeTokens.accentDark,
        contrastText: appThemeTokens.surface,
      },
      success: {
        main: appThemeTokens.success,
        light: appThemeTokens.successLight,
        dark: appThemeTokens.success,
        contrastText: appThemeTokens.surface,
      },
      warning: {
        main: appThemeTokens.warning,
        light: appThemeTokens.warningLight,
        dark: appThemeTokens.warning,
        contrastText: appThemeTokens.textPrimary,
      },
      error: {
        main: appThemeTokens.error,
        light: appThemeTokens.errorLight,
        dark: appThemeTokens.error,
        contrastText: appThemeTokens.surface,
      },
      info: {
        main: appThemeTokens.info,
        light: appThemeTokens.infoLight,
        dark: appThemeTokens.info,
        contrastText: appThemeTokens.surface,
      },
      background: {
        default: appThemeTokens.background,
        paper: appThemeTokens.surface,
      },
      text: {
        primary: appThemeTokens.textPrimary,
        secondary: appThemeTokens.textSecondary,
        disabled: appThemeTokens.textTertiary,
      },
      divider: appThemeTokens.borderLight,
      action: {
        hover: 'rgb(var(--an-primary-rgb) / 0.06)',
        selected: 'rgb(var(--an-primary-rgb) / 0.10)',
        disabledBackground: 'rgb(var(--an-text-primary-rgb) / 0.08)',
      },
    },
    typography: {
      fontFamily: appThemeTokens.fontSans,
      h4: { fontWeight: 800, letterSpacing: 0 },
      h5: { fontWeight: 800, letterSpacing: 0 },
      h6: { fontWeight: 800, letterSpacing: 0 },
      button: { textTransform: 'none', fontWeight: 700 },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: appThemeTokens.background,
            color: appThemeTokens.textPrimary,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            minHeight: 40,
            borderRadius: 'var(--an-radius-sm)',
            padding: '8px 14px',
            textTransform: 'none',
            fontWeight: 700,
            boxShadow: 'none',
            transition: 'background-color var(--an-transition), border-color var(--an-transition), color var(--an-transition), box-shadow var(--an-transition), transform var(--an-transition)',
            '&:hover': {
              transform: 'translateY(-1px)',
              boxShadow: 'none',
            },
            '&.Mui-disabled': {
              transform: 'none',
              boxShadow: 'none',
            },
          },
          contained: {
            boxShadow: 'none',
            '&:hover': {
              boxShadow: 'none',
            },
          },
          containedPrimary: {
            color: 'var(--an-white)',
            backgroundColor: appThemeTokens.primary,
            '&:hover': {
              backgroundColor: appThemeTokens.primaryDark,
              boxShadow: 'none',
            },
          },
          outlined: {
            backgroundColor: appThemeTokens.surface,
          },
          outlinedPrimary: {
            color: appThemeTokens.primary,
            borderColor: appThemeTokens.primary,
            '&:hover': {
              color: 'var(--an-white)',
              backgroundColor: appThemeTokens.primary,
              borderColor: appThemeTokens.primary,
            },
          },
          outlinedSecondary: {
            color: appThemeTokens.primary,
            borderColor: appThemeTokens.primary,
            '&:hover': {
              color: 'var(--an-white)',
              backgroundColor: appThemeTokens.primary,
              borderColor: appThemeTokens.primary,
            },
          },
          text: {
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: appThemeTokens.primaryLight,
              boxShadow: 'none',
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: 'var(--an-radius-sm)',
            color: appThemeTokens.textSecondary,
            transition: 'background-color var(--an-transition), border-color var(--an-transition), color var(--an-transition), box-shadow var(--an-transition), transform var(--an-transition)',
            '&:hover': {
              color: appThemeTokens.primary,
              backgroundColor: appThemeTokens.primaryLight,
              transform: 'translateY(-1px)',
            },
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            borderRadius: 'var(--an-radius-md)',
            marginBottom: theme.spacing(0.5),
            transition: 'background-color 200ms cubic-bezier(0.2, 0, 0, 1), color 200ms cubic-bezier(0.2, 0, 0, 1)',
            '&.Mui-selected': {
              backgroundColor: theme.palette.primary.light,
              color: theme.palette.primary.dark,
              '& .MuiListItemIcon-root': {
                color: theme.palette.primary.dark,
              },
              '&:hover': {
                backgroundColor: theme.palette.primary.light,
              },
            },
          }),
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 'var(--an-radius-md)',
            border: `1px solid ${appThemeTokens.borderLight}`,
            boxShadow: 'var(--an-shadow-sm)',
            backgroundImage: 'none',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          rounded: {
            borderRadius: 'var(--an-radius-md)',
          },
          elevation1: {
            boxShadow: 'var(--an-shadow-sm)',
          },
          elevation8: {
            boxShadow: 'var(--an-shadow-lg)',
            borderRadius: 'var(--an-radius-md)',
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            minHeight: 64,
            color: appThemeTokens.textPrimary,
            backgroundColor: appThemeTokens.surface,
            backgroundImage: 'none',
            borderBottom: `1px solid ${appThemeTokens.borderLight}`,
            boxShadow: 'var(--an-shadow-sm)',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 'var(--an-radius-lg)',
            border: `1px solid ${appThemeTokens.borderLight}`,
            boxShadow: 'var(--an-shadow-lg)',
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontWeight: 800,
            borderBottom: `1px solid ${appThemeTokens.borderLight}`,
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: '16px 24px',
            backgroundColor: appThemeTokens.surfaceMuted,
            borderTop: `1px solid ${appThemeTokens.borderLight}`,
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 'var(--an-radius-sm)',
            backgroundColor: appThemeTokens.surface,
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: appThemeTokens.border,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: appThemeTokens.primary,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: appThemeTokens.primary,
              boxShadow: 'var(--an-focus-ring)',
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: appThemeTokens.textSecondary,
            fontWeight: 700,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 'var(--an-radius-sm)',
            fontWeight: 700,
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            border: `1px solid ${appThemeTokens.borderLight}`,
            boxShadow: 'var(--an-shadow-lg)',
            borderRadius: 'var(--an-radius-md)',
          },
        },
      },
    },
  });
}

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  const { t } = useTranslation('common');

  if (loading) {
    return <LoadingSpinner message={t('loading.authenticating')} />;
  }

  return user ? <>{children}</> : <Navigate to="/login" replace />;
};

// Public Route Component - redirects logged-in users to home
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();
  const { t } = useTranslation('common');

  if (loading) {
    return <LoadingSpinner message={t('loading.default')} />;
  }

  return !user ? <>{children}</> : <Navigate to="/" replace />;
};

// Employee Protected Route Component
const EmployeeProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { employee, loading, mustChangePassword } = useEmployeeAuth();
  const { t } = useTranslation('common');
  const location = useLocation();

  if (loading) {
    return <LoadingSpinner message={t('loading.authenticating')} />;
  }

  if (!employee) {
    return <Navigate to="/employee/login" replace />;
  }

  // If password change is required, force redirect to change-password
  if (mustChangePassword && location.pathname !== '/employee/change-password') {
    return <Navigate to="/employee/change-password" replace />;
  }

  return <>{children}</>;
};

// Employee Public Route Component
const EmployeePublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { employee, loading, mustChangePassword } = useEmployeeAuth();
  const { t } = useTranslation('common');

  if (loading) {
    return <LoadingSpinner message={t('loading.default')} />;
  }

  if (employee) {
    return <Navigate to={mustChangePassword ? '/employee/change-password' : '/employee/dashboard'} replace />;
  }

  return <>{children}</>;
};

// Company Route Component - handles company-specific routing
const CompanyRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { company_id } = useParams<{ company_id: string }>();
  const { companies, currentCompany, setCurrentCompany, companiesLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!companiesLoading && companies.length > 0 && company_id) {
      // Find the company in the available companies
      const decodedCompanyId = decodePathSegment(company_id);
      const targetCompany = companies.find(c => c.company_id === decodedCompanyId);
      
      if (targetCompany) {
        // Set the company if it's different from current
        if (!currentCompany || currentCompany.company_id !== decodedCompanyId) {
          setCurrentCompany(targetCompany);
        }
      } else {
        // Company ID not found, redirect to first available company
        const firstCompany = companies[0];
        const newPath = switchCompanyInUrl(location.pathname, firstCompany.company_id);
        navigate(newPath, { replace: true });
        return;
      }
    }
  }, [company_id, companies, currentCompany, setCurrentCompany, companiesLoading, navigate, location.pathname]);

  const { t } = useTranslation('common');

  if (companiesLoading) {
    return <LoadingSpinner message={t('loading.loadingCompanies')} />;
  }

  if (companies.length === 0) {
    return <>{children}</>;
  }

  return <>{children}</>;
};

/** Preserves bookmarks from /company/:id/ppe-summary â†’ /company/:id/ppe/summary */
const PPESummaryLegacyRedirect: React.FC = () => {
  const { company_id } = useParams<{ company_id: string }>();
  return <Navigate to={`/company/${company_id}/ppe/summary`} replace />;
};

// Root redirect component - redirects to first company's dashboard or no-company dashboard
const RootRedirect: React.FC = () => {
  const { companies, companiesLoading, currentCompany } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!companiesLoading) {
      if (companies.length > 0) {
        // User has companies - redirect to company dashboard
        const targetCompany = currentCompany || companies[0];
        navigate(buildCompanyUrl(targetCompany.company_id, 'dashboard'), { replace: true });
      } else {
        // User has no companies - redirect to no-company dashboard
        navigate('/no-company/dashboard', { replace: true });
      }
    }
  }, [companies, companiesLoading, currentCompany, navigate]);

  const { t } = useTranslation('common');

  if (companiesLoading) {
    return <LoadingSpinner message={t('loading.default')} />;
  }

  return <LoadingSpinner message={t('loading.redirecting')} />;
};

const NotificationRootRedirect: React.FC = () => {
  const { companies, companiesLoading, currentCompany } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!companiesLoading) {
      if (companies.length > 0) {
        const targetCompany = currentCompany || companies[0];
        navigate(buildCompanyUrl(targetCompany.company_id, 'notifications'), { replace: true });
      } else {
        navigate('/no-company/dashboard', { replace: true });
      }
    }
  }, [companies, companiesLoading, currentCompany, navigate]);

  const { t } = useTranslation('common');

  if (companiesLoading) {
    return <LoadingSpinner message={t('loading.default')} />;
  }

  return <LoadingSpinner message={t('loading.redirecting')} />;
};

const HierarchyRedirect: React.FC = () => {
  const { company_id } = useParams<{ company_id?: string }>();
  const target = company_id ? buildCompanyUrl(company_id, 'settings/hierarchy') : '/settings/hierarchy';
  return <Navigate to={target} replace />;
};

// Component to handle intended path redirects
const PathHandler: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check if there's an intended path stored from error.html redirect
    const intendedPath = sessionStorage.getItem('intendedPath');
    
    if (intendedPath && location.pathname === '/') {
      // Clear the stored path
      sessionStorage.removeItem('intendedPath');
      
      // Navigate to the intended path
      navigate(intendedPath, { replace: true });
    }
  }, [navigate, location]);

  return null;
};

const AdminRoutes: React.FC = () => {
  return (
    <AuthProvider>
      <Routes>
        {/* Guest Routes - only accessible when not logged in */}
        <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
        <Route path="/forgot-password" element={<PublicRoute><ForgotPassword /></PublicRoute>} />
        <Route path="/reset-password" element={<ResetPassword />} />

        {/* No Company Routes - for users not assigned to any company */}
        <Route
          path="/no-company/dashboard"
          element={
            <ProtectedRoute>
              <React.Suspense fallback={<LoadingSpinner />}>
                <Dashboard />
              </React.Suspense>
            </ProtectedRoute>
          }
        />

        {/* Account Settings Route */}
        <Route
          path="/account"
          element={
            <ProtectedRoute>
              <AccountSettings />
            </ProtectedRoute>
          }
        />

        {/* Super Admin Routes - accessible without company assignment */}
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings/hierarchy"
          element={
            <ProtectedRoute>
              <HierarchyManagement />
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings/roles"
          element={
            <ProtectedRoute>
              <RoleManagement />
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings/users"
          element={
            <ProtectedRoute>
              <UserManagement />
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings/assignments"
          element={
            <ProtectedRoute>
              <UserCompanyAssignments />
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings/companies"
          element={
            <ProtectedRoute>
              <CompanyManagement />
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings/groups"
          element={<HierarchyRedirect />}
        />

        <Route
          path="/settings/locations"
          element={<HierarchyRedirect />}
        />

        <Route
          path="/settings/appearance-thresholds"
          element={
            <ProtectedRoute>
              <AppearanceThresholds />
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings/ppe-settings"
          element={
            <ProtectedRoute>
              <PPEDepartmentSettings />
            </ProtectedRoute>
          }
        />

        <Route
          path="/ppe-violations"
          element={
            <ProtectedRoute>
              <PPEViolations />
            </ProtectedRoute>
          }
        />

        {/* Company-specific Protected Routes */}
        <Route
          path="/company/:company_id/dashboard"
          element={
            <ProtectedRoute>
              <CompanyRoute>
                <React.Suspense fallback={<LoadingSpinner />}>
                  <Dashboard />
                </React.Suspense>
              </CompanyRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/company/:company_id/analytics"
          element={
            <ProtectedRoute>
              <CompanyRoute>
                <React.Suspense fallback={<LoadingSpinner />}>
                  <Analytics />
                </React.Suspense>
              </CompanyRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/company/:company_id/vacations"
          element={
            <ProtectedRoute>
              <CompanyRoute>
                <Vacations />
              </CompanyRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/company/:company_id/holidays"
          element={
            <ProtectedRoute>
              <CompanyRoute>
                <Holidays />
              </CompanyRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/company/:company_id/cameras"
          element={
            <ProtectedRoute>
              <CompanyRoute>
                <Cameras />
              </CompanyRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/company/:company_id/employees"
          element={
            <ProtectedRoute>
              <CompanyRoute>
                <React.Suspense fallback={<LoadingSpinner />}>
                  <Employees />
                </React.Suspense>
              </CompanyRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/company/:company_id/departments"
          element={
            <ProtectedRoute>
              <CompanyRoute>
                <Departments />
              </CompanyRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/company/:company_id/calendars"
          element={
            <ProtectedRoute>
              <CompanyRoute>
                <Calendars />
              </CompanyRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/company/:company_id/scheduling"
          element={
            <ProtectedRoute>
              <CompanyRoute>
                <React.Suspense fallback={<LoadingSpinner />}>
                  <Scheduling />
                </React.Suspense>
              </CompanyRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/company/:company_id/edge-devices"
          element={
            <ProtectedRoute>
              <CompanyRoute>
                <EdgeDevices />
              </CompanyRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/company/:company_id/daily-attendance"
          element={
            <ProtectedRoute>
              <CompanyRoute>
                <React.Suspense fallback={<LoadingSpinner />}>
                  <DailyAttendance />
                </React.Suspense>
              </CompanyRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/company/:company_id/employee-timeline"
          element={
            <ProtectedRoute>
              <CompanyRoute>
                <React.Suspense fallback={<LoadingSpinner />}>
                  <EmployeeTimeline />
                </React.Suspense>
              </CompanyRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/company/:company_id/reports"
          element={
            <ProtectedRoute>
              <CompanyRoute>
                <React.Suspense fallback={<LoadingSpinner />}>
                  <Reports />
                </React.Suspense>
              </CompanyRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/company/:company_id/custom-reports"
          element={
            <ProtectedRoute>
              <CompanyRoute>
                <React.Suspense fallback={<LoadingSpinner />}>
                  <CustomReports />
                </React.Suspense>
              </CompanyRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/company/:company_id/snapshots"
          element={
            <ProtectedRoute>
              <CompanyRoute>
                <React.Suspense fallback={<LoadingSpinner />}>
                  <CameraSnapshots />
                </React.Suspense>
              </CompanyRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/company/:company_id/settings/snapshot-settings"
          element={
            <ProtectedRoute>
              <CompanyRoute>
                <SnapshotSettings />
              </CompanyRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/company/:company_id/settings/analysis-prompts"
          element={
            <ProtectedRoute>
              <CompanyRoute>
                <AnalysisPrompts />
              </CompanyRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/company/:company_id/settings/desk-zones"
          element={
            <ProtectedRoute>
              <CompanyRoute>
                <DeskZones />
              </CompanyRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/company/:company_id/desk-occupancy"
          element={
            <ProtectedRoute>
              <CompanyRoute>
                <React.Suspense fallback={<LoadingSpinner />}>
                  <DeskOccupancy />
                </React.Suspense>
              </CompanyRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/company/:company_id/ppe/detections"
          element={
            <ProtectedRoute>
              <CompanyRoute>
                <PPEDetections />
              </CompanyRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/company/:company_id/ppe/summary"
          element={
            <ProtectedRoute>
              <CompanyRoute>
                <PPESummary />
              </CompanyRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/company/:company_id/ppe-summary"
          element={
            <ProtectedRoute>
              <CompanyRoute>
                <PPESummaryLegacyRedirect />
              </CompanyRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/company/:company_id/ppe"
          element={
            <ProtectedRoute>
              <CompanyRoute>
                <PPE />
              </CompanyRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/company/:company_id/policies"
          element={
            <ProtectedRoute>
              <CompanyRoute>
                <Policies />
              </CompanyRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/company/:company_id/face-validation"
          element={
            <ProtectedRoute>
              <CompanyRoute>
                <React.Suspense fallback={<LoadingSpinner />}>
                  <FaceValidation />
                </React.Suspense>
              </CompanyRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/company/:company_id/notifications"
          element={
            <ProtectedRoute>
              <CompanyRoute>
                <Notifications />
              </CompanyRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/company/:company_id/settings"
          element={
            <ProtectedRoute>
              <CompanyRoute>
                <Settings />
              </CompanyRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/company/:company_id/settings/hierarchy"
          element={
            <ProtectedRoute>
              <CompanyRoute>
                <HierarchyManagement />
              </CompanyRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/company/:company_id/settings/roles"
          element={
            <ProtectedRoute>
              <CompanyRoute>
                <RoleManagement />
              </CompanyRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/company/:company_id/settings/users"
          element={
            <ProtectedRoute>
              <CompanyRoute>
                <UserManagement />
              </CompanyRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/company/:company_id/settings/assignments"
          element={
            <ProtectedRoute>
              <CompanyRoute>
                <UserCompanyAssignments />
              </CompanyRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/company/:company_id/settings/companies"
          element={
            <ProtectedRoute>
              <CompanyRoute>
                <CompanyManagement />
              </CompanyRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/company/:company_id/settings/groups"
          element={<HierarchyRedirect />}
        />

        <Route
          path="/company/:company_id/settings/locations"
          element={<HierarchyRedirect />}
        />

        <Route
          path="/company/:company_id/settings/appearance-thresholds"
          element={
            <ProtectedRoute>
              <CompanyRoute>
                <AppearanceThresholds />
              </CompanyRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/company/:company_id/settings/ppe-settings"
          element={
            <ProtectedRoute>
              <CompanyRoute>
                <PPEDepartmentSettings />
              </CompanyRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/company/:company_id/ppe-violations"
          element={
            <ProtectedRoute>
              <CompanyRoute>
                <PPEViolations />
              </CompanyRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/company/:company_id/payroll/settings"
          element={
            <ProtectedRoute>
              <CompanyRoute>
                <React.Suspense fallback={<LoadingSpinner />}>
                  <PayrollSettings />
                </React.Suspense>
              </CompanyRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/company/:company_id/payroll/pay-components"
          element={
            <ProtectedRoute>
              <CompanyRoute>
                <React.Suspense fallback={<LoadingSpinner />}>
                  <PayComponents />
                </React.Suspense>
              </CompanyRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/company/:company_id/payroll/exceptions"
          element={
            <ProtectedRoute>
              <CompanyRoute>
                <React.Suspense fallback={<LoadingSpinner />}>
                  <PayrollExceptions />
                </React.Suspense>
              </CompanyRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/company/:company_id/payroll/periods"
          element={
            <ProtectedRoute>
              <CompanyRoute>
                <React.Suspense fallback={<LoadingSpinner />}>
                  <PayrollPeriods />
                </React.Suspense>
              </CompanyRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/company/:company_id/payroll/periods/:period_id"
          element={
            <ProtectedRoute>
              <CompanyRoute>
                <React.Suspense fallback={<LoadingSpinner />}>
                  <PayrollPeriodDetail />
                </React.Suspense>
              </CompanyRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/company/:company_id/payroll/payslips/:payslip_id"
          element={
            <ProtectedRoute>
              <CompanyRoute>
                <React.Suspense fallback={<LoadingSpinner />}>
                  <PayslipDetail />
                </React.Suspense>
              </CompanyRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings/payroll-overview"
          element={
            <ProtectedRoute>
              <React.Suspense fallback={<LoadingSpinner />}>
                <PayrollOverview />
              </React.Suspense>
            </ProtectedRoute>
          }
        />

        {/* Legacy route redirects - for backward compatibility */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <RootRedirect />
            </ProtectedRoute>
          }
        />
        <Route
          path="/analytics"
          element={
            <ProtectedRoute>
              <RootRedirect />
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <RootRedirect />
            </ProtectedRoute>
          }
        />
        <Route
          path="/cameras"
          element={
            <ProtectedRoute>
              <RootRedirect />
            </ProtectedRoute>
          }
        />
        <Route
          path="/employees"
          element={
            <ProtectedRoute>
              <RootRedirect />
            </ProtectedRoute>
          }
        />
        <Route
          path="/departments"
          element={
            <ProtectedRoute>
              <RootRedirect />
            </ProtectedRoute>
          }
        />
        <Route
          path="/calendars"
          element={
            <ProtectedRoute>
              <RootRedirect />
            </ProtectedRoute>
          }
        />
        <Route
          path="/edge-devices"
          element={
            <ProtectedRoute>
              <RootRedirect />
            </ProtectedRoute>
          }
        />

        {/* Remove legacy non-company timeline route */}
        <Route
          path="/reports"
          element={
            <ProtectedRoute>
              <RootRedirect />
            </ProtectedRoute>
          }
        />
        <Route
          path="/face-validation"
          element={
            <ProtectedRoute>
              <RootRedirect />
            </ProtectedRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <NotificationRootRedirect />
            </ProtectedRoute>
          }
        />

        {/* Default redirect */}
        <Route
          path="/" 
          element={
            <ProtectedRoute>
              <RootRedirect />
            </ProtectedRoute>
          }
        />
      </Routes>
    </AuthProvider>
  );
};

function App() {
  const { i18n } = useTranslation();
  const isRtl = isRtlLanguage(i18n.language);
  const theme = useMemo(() => createAppTheme(isRtl ? 'rtl' : 'ltr'), [isRtl]);
  const cache = isRtl ? rtlCache : ltrCache;

  return (
    <CacheProvider value={cache}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <EmployeeAuthProvider>
            <Router>
            <PathHandler />
            <Routes>
              {/* Employee Portal Routes */}
              <Route path="/employee/login" element={<EmployeePublicRoute><EmployeeLogin /></EmployeePublicRoute>} />
              <Route path="/employee/change-password" element={<EmployeeProtectedRoute><EmployeeChangePassword /></EmployeeProtectedRoute>} />
              <Route path="/employee/dashboard" element={<EmployeeProtectedRoute><EmployeeDashboard /></EmployeeProtectedRoute>} />
              <Route path="/employee/notifications" element={<EmployeeProtectedRoute><EmployeeNotifications /></EmployeeProtectedRoute>} />
              <Route path="/employee/payslips" element={<EmployeeProtectedRoute><React.Suspense fallback={<LoadingSpinner />}><EmployeePayslips /></React.Suspense></EmployeeProtectedRoute>} />
              <Route path="/employee/payslips/:id" element={<EmployeeProtectedRoute><React.Suspense fallback={<LoadingSpinner />}><EmployeePayslipDetail /></React.Suspense></EmployeeProtectedRoute>} />
              <Route path="/employee" element={<Navigate to="/employee/login" replace />} />

              {/* All other routes are handled by AdminRoutes with its own AuthProvider */}
              <Route path="*" element={<AdminRoutes />} />
            </Routes>
          </Router>
        </EmployeeAuthProvider>
      </ThemeProvider>
    </CacheProvider>
  );
}

export default App;
